import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Globe, Share, Smartphone, X } from 'lucide-react';

function useBodyScrollLock(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [enabled]);
}

export default function InstallGuideModal({ open, initialPlatform = 'ios', isIOSBrowser = false, theme = 'dark', language = 'es', onClose }) {
  const [platform, setPlatform] = useState(initialPlatform === 'android' ? 'android' : 'ios');

  useEffect(() => {
    if (open) setPlatform(initialPlatform === 'android' ? 'android' : 'ios');
  }, [open, initialPlatform]);

  useBodyScrollLock(open);

  if (!open) return null;

  const isIOS = platform === 'ios';
  const copy = language === 'es' ? {
    title: 'Instalá WannaColive',
    subtitle: 'Elegí tu dispositivo y seguí estos pasos.',
    ios: 'iOS',
    android: 'Android',
    safariNote: 'Para instalarla en iPhone o iPad, abrí esta página en Safari.',
    iosSteps: [
      'Abrí WannaColive en Safari.',
      'Tocá el botón Compartir.',
      'Elegí “Agregar a pantalla de inicio”.',
      'Tocá “Agregar” y abrí WannaColive desde el nuevo ícono.',
    ],
    androidSteps: [
      'Abrí WannaColive en Chrome.',
      'Tocá el menú ⋮.',
      'Elegí “Instalar app” o “Agregar a pantalla principal”.',
      'Confirmá la instalación.',
    ],
    iosHint: 'La instalación se realiza desde el menú Compartir de Safari.',
    androidHint: 'El nombre de la opción puede variar según el navegador.',
    finalHint: 'Después, abrí WannaColive desde el ícono de tu pantalla de inicio.',
  } : {
    title: 'Install WannaColive',
    subtitle: 'Choose your device and follow these steps.',
    ios: 'iOS',
    android: 'Android',
    safariNote: 'To install it on iPhone or iPad, open this page in Safari.',
    iosSteps: [
      'Open WannaColive in Safari.',
      'Tap the Share button.',
      'Choose “Add to Home Screen”.',
      'Tap “Add” and open WannaColive from the new icon.',
    ],
    androidSteps: [
      'Open WannaColive in Chrome.',
      'Tap the ⋮ menu.',
      'Choose “Install app” or “Add to Home screen”.',
      'Confirm the installation.',
    ],
    iosHint: 'Installation is done from Safari’s Share menu.',
    androidHint: 'The option name may vary depending on the browser.',
    finalHint: 'Then, open WannaColive from the icon on your Home Screen.',
  };
  const steps = isIOS ? copy.iosSteps : copy.androidSteps;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-3 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="install-guide-title"
      data-testid="install-guide-modal"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className={`flex max-h-[min(72dvh,42rem)] w-full max-w-md min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-surface ${theme === 'dark' ? 'shadow-none' : 'shadow-2xl'}`}>
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground">
              <Smartphone className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h2 id="install-guide-title" className="font-heading text-lg font-semibold text-foreground">{copy.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{copy.subtitle}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="min-h-0 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-2 gap-2 rounded-xl bg-muted p-1" role="tablist" aria-label="Dispositivo">
            <button type="button" role="tab" aria-selected={isIOS} onClick={() => setPlatform('ios')} className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${isIOS ? 'bg-surface text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              {copy.ios}
            </button>
            <button type="button" role="tab" aria-selected={!isIOS} onClick={() => setPlatform('android')} className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${!isIOS ? 'bg-surface text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              {copy.android}
            </button>
          </div>

          {isIOS && isIOSBrowser && (
            <p className="mt-4 rounded-xl border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              {copy.safariNote}
            </p>
          )}

          <ol className="mt-5 space-y-3">
            {steps.map((step, index) => (
              <li key={step} className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-semibold text-background">{index + 1}</span>
                <p className="pt-1 text-sm leading-relaxed text-foreground">{step}</p>
              </li>
            ))}
          </ol>

          {isIOS ? (
            <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
              <Share className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{copy.iosHint}</span>
            </div>
          ) : (
            <div className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
              <Globe className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{copy.androidHint}</span>
            </div>
          )}

          <div className="mt-5 flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{copy.finalHint}</span>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
